/*
 * stockup-controller.js — purpose-built variant controller for the
 * Fresh Meals Stock Up Box PDP (Flavor × Box Size, one-time purchase).
 *
 * Fixes the shared-theme bug by design: resolves the variant from BOTH
 * the selected Flavor AND Box Size, repaints price / compare-at / savings
 * per box-size card, updates the ?variant= URL, and keeps the add-to-cart
 * form's variant id in sync. No Freeze-Dried-Raw logic.
 *
 * ALSO wires the Add to Cart button into the theme's AJAX cart. The custom
 * stock-up form is not a {% form 'product' %}, so the theme's cart handler
 * never bound to it — a native submit bounced the shopper to the home page
 * instead of adding the item. We intercept the submit, add via /cart/add.js,
 * then open the cart drawer the same way the site's cart icon does.
 */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    var root = document.querySelector('[data-stockup]');
    if (!root) return;

    var dataEl = root.querySelector('[data-stockup-variants]');
    var variants = [];
    try { variants = JSON.parse(dataEl.textContent.trim()); } catch (e) { return; }

    var optionNames = [];
    try { optionNames = JSON.parse(root.getAttribute('data-option-names')); } catch (e) {}

    var idInput = root.querySelector('[data-stockup-id]');
    var atcBtn = root.querySelector('[data-stockup-atc]');
    var form = root.querySelector('[data-stockup-form]') || (atcBtn && atcBtn.closest('form'));

    function cssName(n) {
      return window.CSS && CSS.escape ? CSS.escape(n) : n;
    }

    function selectedValues() {
      return optionNames.map(function (n) {
        var el = root.querySelector('input[name="' + cssName(n) + '"]:checked');
        return el ? el.value : null;
      });
    }

    function findVariant(values) {
      return variants.find(function (v) {
        return v.options.length === values.length &&
          v.options.every(function (o, i) { return o === values[i]; });
      }) || null;
    }

    function money(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    // Repaint each box-size card's price/compare/savings for the current flavor
    function repaintSizeCards(currentFlavor) {
      var cards = root.querySelectorAll('[data-size-card]');
      cards.forEach(function (card) {
        var size = card.getAttribute('data-size-card');
        var v = variants.find(function (x) {
          return x.options[0] === currentFlavor && x.options[1] === size;
        });
        if (!v) return;
        var priceEl = card.querySelector('[data-size-price]');
        var compareEl = card.querySelector('[data-size-compare]');
        var saveEl = card.querySelector('[data-size-save]');
        if (priceEl) priceEl.textContent = money(v.price);
        if (compareEl) {
          if (v.compareAt > v.price) { compareEl.textContent = money(v.compareAt); compareEl.hidden = false; }
          else { compareEl.hidden = true; }
        }
        if (saveEl) {
          var save = v.compareAt - v.price;
          if (save > 0 && v.compareAt > 0) {
            saveEl.textContent = 'SAVE ' + Math.round((save / v.compareAt) * 100) + '%';
            saveEl.hidden = false;
          } else { saveEl.hidden = true; }
        }
        // disable unavailable
        var input = card.querySelector('input');
        if (input) input.disabled = !v.available;
      });
    }

    // Flavor-driven visuals: selected icon = full hue, others = light tint,
    // and the gallery background takes the selected flavor's light tint.
    function paintFlavorVisuals() {
      var gal = root.querySelector('[data-stockup-gallery]');
      var galBg = null;
      root.querySelectorAll('.stockup-flavor-icon').forEach(function (icon) {
        var lbl = icon.closest('.stockup-flavor');
        var input = lbl ? lbl.querySelector('input') : null;
        var on = icon.getAttribute('data-bg-full');
        var off = icon.getAttribute('data-bg-light');
        if (input && input.checked) { if (on) icon.style.background = on; galBg = off; }
        else if (off) { icon.style.background = off; }
      });
      if (gal && galBg) gal.style.background = galBg;
    }

    function update() {
      var values = selectedValues();
      var flavorIdx = optionNames.indexOf('Flavor');
      var currentFlavor = flavorIdx > -1 ? values[flavorIdx] : values[0];
      repaintSizeCards(currentFlavor);
      paintFlavorVisuals();

      // Sync selected-state classes so ONLY the chosen flavor / size shows as
      // active. Radios are single-select, but the server-rendered `is-active`
      // class stays on the default option unless we clear it here — which is
      // why both box sizes looked selected after switching.
      root.querySelectorAll('.stockup-flavor').forEach(function (l) {
        var i = l.querySelector('input'); l.classList.toggle('is-active', !!(i && i.checked));
      });
      root.querySelectorAll('.stockup-size').forEach(function (l) {
        var i = l.querySelector('input'); l.classList.toggle('is-active', !!(i && i.checked));
      });

      var v = findVariant(values);
      if (!v) return;

      if (idInput) idInput.value = v.id;
      if (atcBtn) {
        atcBtn.disabled = !v.available;
        var label = atcBtn.querySelector('[data-stockup-atc-label]') || atcBtn;
        label.textContent = v.available ? 'Add to Cart' : 'Sold Out';
      }

      // Keep the URL shareable / correct
      try {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        history.replaceState({}, '', u.toString());
      } catch (e) {}
    }

    // Open the theme's cart drawer the same way the header cart icon does
    // (this triggers the theme's own open + refresh). Falls back to the cart
    // page if the opener isn't present.
    function openCart() {
      var opener = document.querySelector('.js-drawer-open-cart');
      if (opener) { opener.click(); return; }
      if (typeof window.showDrawer === 'function') { window.showDrawer(); return; }
      window.location.href = '/cart';
    }

    // Intercept the native submit and add via AJAX so the shopper is never
    // bounced to the home page.
    function bindAddToCart() {
      if (!form || form.dataset.stockupBound === '1') return;
      form.dataset.stockupBound = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (form.dataset.busy === '1') return;
        form.dataset.busy = '1';
        if (atcBtn) atcBtn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        })
          .then(function (r) {
            return r.json().then(function (j) {
              if (!r.ok) { throw new Error((j && j.description) || 'Add to cart failed'); }
              return j;
            });
          })
          .then(function () { openCart(); })
          .catch(function () { window.location.href = '/cart'; })
          .finally(function () {
            form.dataset.busy = '';
            if (atcBtn) atcBtn.disabled = false;
          });
      });
    }

    // Recompute on any Flavor / Box Size change
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.name && optionNames.indexOf(t.name) !== -1) {
        update();
      }
    });

    update();
    bindAddToCart();
  }
})();